import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatLocalDateTime } from '../utils/dateUtils';
import { eventService, EventType } from '../services/eventService';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MapIcon from '@mui/icons-material/Map';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiClient from '../services/apiService';
import { suspensionApi, SuspensionSummary } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { t } from 'i18next';

// Helper function to format error messages
const formatErrorMessage = (error: any): string => {
  // Handle FastAPI validation errors - array of objects with loc, msg, type
  if (error?.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    return error.response.data.detail
      .map((err: any) => err.msg || JSON.stringify(err))
      .join('. ');
  }
  
  // Handle FastAPI validation errors - object with loc, msg, type
  if (error?.response?.data?.detail && typeof error.response.data.detail === 'object' && 'msg' in error.response.data.detail) {
    return error.response.data.detail.msg;
  }
  
  // Handle string error details
  if (error?.response?.data?.detail && typeof error.response.data.detail === 'string') {
    return error.response.data.detail;
  }
  
  // Handle generic error message
  if (error?.message) {
    return error.message;
  }
  
  // Fallback
  return t('errors.genericError');
};

// Interface for sensor summary
interface SensorSummary {
  total: number;
  active: number;
  offline: number;
  withAlerts: number;
}

// Interface for alert summary
interface AlertSummary {
  total: number;
  unacknowledged: number;
  recent: number;
}

// Interface for recent alert
interface RecentAlert {
  id: number;
  sensor_id: number;
  sensor_name: string;
  event_type: string;
  alert_level?: number;
  created_at: string;
  is_acknowledged: boolean;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sensorSummary, setSensorSummary] = useState<SensorSummary>({
    total: 0,
    active: 0,
    offline: 0,
    withAlerts: 0
  });
  const [alertSummary, setAlertSummary] = useState<AlertSummary>({
    total: 0,
    unacknowledged: 0,
    recent: 0
  });
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([]);
  const [suspensionSummary, setSuspensionSummary] = useState<SuspensionSummary>({ active: 0, scheduled: 0 });

  // Function to fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (isAuthenticated) {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch sensor summary
        const sensorResponse = await apiClient.get('/sensors/summary');
        setSensorSummary(sensorResponse.data);
        
        // Fetch alert summary
        const alertResponse = await apiClient.get('/alerts/summary');
        setAlertSummary(alertResponse.data);
        
        // Fetch recent alerts
        const recentAlertsResponse = await apiClient.get('/alerts/recent/unacknowledged');
        setRecentAlerts(recentAlertsResponse.data);

        // Fetch suspension summary
        const suspSummaryResponse = await suspensionApi.summary();
        setSuspensionSummary(suspSummaryResponse.data);
        
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(
          formatErrorMessage(err) || 
          t('common.failedToLoad', { resource: t('dashboard.title').toLowerCase() })
        );
      } finally {
        setLoading(false);
      }
    }
  }, [isAuthenticated, t]);

  // Initial data load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Subscribe to new alert events
  useEffect(() => {
    if (isAuthenticated) {
      // Set up event listener for new alerts
      const unsubscribe = eventService.subscribe(EventType.NEW_ALERT, () => {
        // Refresh dashboard data when a new alert is received
        fetchDashboardData();
      });
      
      // Clean up subscription when component unmounts
      return () => {
        unsubscribe();
      };
    }
  }, [isAuthenticated, fetchDashboardData]);

  // Handle click on summary cards
  const handleSensorsClick = () => navigate('/sensors');
  const handleAlertsClick = () => navigate('/alerts');
  const handleMapClick = () => navigate('/map');
  const handleSuspensionsClick = () => navigate('/suspensions');
  
  // Handle click on alert item
  const handleAlertClick = (alertId: number) => {
    navigate(`/alerts/${alertId}`);
  };

  if (loading && !recentAlerts.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('dashboard.title')}
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Sensors Summary */}
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardActionArea onClick={handleSensorsClick}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SensorsIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">{t('dashboard.sensors')}</Typography>
                </Box>
                <Typography variant="h3" sx={{ textAlign: 'center', my: 2 }}>
                  {sensorSummary.active}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    label={t('dashboard.active')}
                    size="small"
                    color="success"
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        
        {/* Alerts Summary */}
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardActionArea onClick={handleAlertsClick}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <NotificationsActiveIcon sx={{ fontSize: 28, mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6">{t('dashboard.alerts')}</Typography>
                </Box>
                <Typography variant="h3" sx={{ textAlign: 'center', my: 2 }}>
                  {alertSummary.total}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Chip 
                    label={`${alertSummary.unacknowledged} ${t('dashboard.unacknowledged')}`} 
                    size="small" 
                    color="warning"
                  />
                  <Chip 
                    label={`${alertSummary.recent} ${t('dashboard.recent')}`} 
                    size="small" 
                    color="info"
                  />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        
        {/* Map Summary */}
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardActionArea onClick={handleMapClick}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MapIcon sx={{ fontSize: 28, mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">{t('dashboard.sensorMap')}</Typography>
                </Box>
                <Typography variant="body1" sx={{ textAlign: 'center', my: 2 }}>
                  {t('dashboard.viewOnMap')}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip 
                    label={`${sensorSummary.withAlerts} ${t('dashboard.withAlerts')}`} 
                    size="small" 
                    color="error"
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* Suspension Summary */}
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardActionArea onClick={handleSuspensionsClick}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PauseCircleOutlineIcon sx={{ fontSize: 28, mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6">{t('suspensions.dashboardCard.title')}</Typography>
                </Box>
                <Typography variant="h3" sx={{ textAlign: 'center', my: 2 }}>
                  {suspensionSummary.active}
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    label={t('suspensions.dashboardCard.scheduledSuffix', { n: suspensionSummary.scheduled })}
                    size="small"
                    color="primary"
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
      
      {/* Recent Alerts */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('dashboard.recentAlerts')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {recentAlerts.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
            {t('dashboard.noRecentAlerts')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {recentAlerts.map((alert) => (
              <Paper 
                key={alert.id} 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider', 
                  '&:hover': { bgcolor: 'action.hover' } 
                }}
                onClick={() => handleAlertClick(alert.id)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {alert.is_acknowledged ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                    )}
                    <Box>
                      <Typography variant="subtitle1">
                        {t(`eventTypes.${alert.event_type}`, t('eventTypes.default'))} - {alert.sensor_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatLocalDateTime(alert.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {alert.alert_level !== null && alert.alert_level !== undefined && (
                      <Chip
                        label={`${t('alerts.level')} ${alert.alert_level}`}
                        color={alert.alert_level >= 3 ? 'error' : alert.alert_level >= 2 ? 'warning' : 'default'}
                        size="small"
                      />
                    )}
                    <Chip
                      label={alert.is_acknowledged ? t('dashboard.acknowledged') : t('dashboard.unacknowledged')}
                      color={alert.is_acknowledged ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
        
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleAlertsClick}
          >
            {t('dashboard.viewAllAlerts')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;